// DashboardPage — Correção UX v2 (§5.1 + W1.2): réplica LITERAL do wireframe
// docs/RAIOS-UX-SIDEBAR-SPEC.html, aba "02 Dashboard".
//
// Anatomia (markup/classes portadas verbatim — wireframe.css + wf-dash.css):
//   1. .wf-dash-cards  — 4 cards .wf-mc (Negócios · Agentes · Skills · Pipelines)
//   2. .wf-dash-mid    — Assuntos principais de hoje (.wf-dblock, full-width, top-3)
//   3. .wf-dbottom     — Demandas (.wf-statgrid 24h + .wf-twolist) + Atividade (.wf-feed)
//
// Dados (W1.2 — blocos independentes em PARALELO, sem per_page:200):
//   - overview block: GET /v1/dashboard/overview (counts/today_topics/
//     demands_24h_by_status) + fallbacks pré-restart
//   - demands block: listDemands enxutos por status canônico das listas
//     (running+finishing / pending_approval+review — DEM-2592 parity card×lista,
//     per_page 20) + listPipelineRuns (Etapa X/Y real — C3)
//   - atividade: feed realtime do mc-store (independente)
// Realtime refetch POR BLOCO com coalescing de 2s (nada de recarregar a página
// inteira por evento).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  api,
  ApiError,
  type Business,
  type DashboardOverview,
  type Demand,
} from '../lib/api';
import { listAgentsCached, listSkillsCached } from '@/lib/meta-cache';
import { listPipelineRuns, listPipelines, type PipelineRunSummary } from '../lib/api-pipelines';
import { fmtDayTime, fmtNumber } from '../lib/format';
import { useAuth } from '../context/AuthContext';
import { useRealtimeDashboard } from '../hooks/useRealtimeDashboard';
import { useSupabaseRealtime, type RealtimeEventPayload } from '../hooks/useSupabaseRealtime';
import { useCoalesced } from '../hooks/useCoalesced';
import { useMCStore } from '@/stores/mc-store';
import { formatActorLead, type ActivityItem } from '@/lib/activity';
import { cn } from '@/lib/utils';
import { WfEmpty, WfKpi, type WfKpiTone } from '@/components/wf';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Select } from '@/components/ui/Select';
import { Skeleton } from '@/components/ui/Skeleton';
import { Textarea } from '@/components/ui/Textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/Dialog';
import { useToast } from '@/components/ui/Toast';
import { PageHeader } from '@/components/layout/PageHeader';
import { DemandDetailPanel } from './mc/DemandDetailPanel';
import { DemandRichCard } from './mc/DemandRichCard';
import { stepInfoFromRun } from './mc/demand-step-info';
import { WaitingApprovalCard } from '@/components/demands/WaitingApprovalCard';
import {
  demandAwaitsApproval,
  demandIsInProgress,
  IN_PROGRESS_STATUSES,
  WAITING_APPROVAL_STATUSES,
} from '@/pages/mc/demand-constants';
import { ElapsedClock } from '@/components/demands/ElapsedClock';
import { Activity as ActivityIcon, AlertCircle, Bot, Briefcase, Check, Clock, MessagesSquare, Package, RefreshCw, Sparkles, Workflow, X, type LucideIcon,  } from 'lucide-react';
import '../styles/wf-dash.css';

/* ═══ Types ═════════════════════════════════════════════════════════════════ */

type CountsBlock = NonNullable<DashboardOverview['counts']>;
type TodayTopics = NonNullable<DashboardOverview['today_topics']>;
type Demands24h = NonNullable<DashboardOverview['demands_24h_by_status']>;

/* ═══ Helpers ═══════════════════════════════════════════════════════════════ */

// Hora isolada ("Início 14:05" / "Em aprov. 15:40" — copy literal do wireframe).
// format.ts não expõe hora-sem-dia (fmtDayTime inclui "dd/mm,"); mesmo Intl
// pt-BR/fuso local do format.ts, instanciado uma vez.
const timeOnlyFmt = new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' });
function fmtTimeOnly(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return null;
  return timeOnlyFmt.format(d);
}

/**
 * Derivação client-side das contagens da linha 1 quando `overview.counts`
 * ausente (backend pré-restart — fallback OBRIGATÓRIO do §6.1).
 */
async function deriveCountsFallback(): Promise<CountsBlock | null> {
  const [biz, agents, skills, pipelines] = await Promise.allSettled([
    api.listBusinesses(),
    listAgentsCached(),
    listSkillsCached(),
    listPipelines(),
  ]);
  if (
    biz.status === 'rejected' &&
    agents.status === 'rejected' &&
    skills.status === 'rejected' &&
    pipelines.status === 'rejected'
  ) {
    return null;
  }
  const b = biz.status === 'fulfilled' ? biz.value : [];
  const a = agents.status === 'fulfilled' ? agents.value : [];
  const s = skills.status === 'fulfilled' ? skills.value : [];
  const p = pipelines.status === 'fulfilled' ? pipelines.value : [];
  return {
    businesses: {
      total: b.length,
      draft: b.filter((x) => x.lifecycle_status === 'draft').length,
      active: b.filter((x) => x.lifecycle_status === 'active').length,
    },
    agents: {
      total: a.length,
      tenant: a.filter((x) => x.scope !== 'global').length,
      global: a.filter((x) => x.scope === 'global').length,
    },
    skills: {
      total: s.length,
      tenant: s.filter((x) => x.scope !== 'global').length,
      global: s.filter((x) => x.scope === 'global').length,
    },
    pipelines: {
      total: p.length,
      tenant: p.filter((x) => x.scope_label === 'tenant').length,
      global: p.filter((x) => x.scope_label === 'global').length,
    },
  };
}

/**
 * Derivação client-side da métrica 24h quando `overview.demands_24h_by_status`
 * ausente. Agrupa os status canônicos nos 7 buckets do wireframe usando o
 * MESMO mapeamento do backend (dashboard.service.ts,
 * DEMAND_STATUS_TO_DASHBOARD_KEY): draft/planned/ready→ready ·
 * running/finishing→running · pending_approval/review→pending_approval ·
 * approved→approved · done→done · rejected/cancelled→rejected ·
 * blocked/failed→blocked.
 *
 * SEMÂNTICA 24h (DEM-2592): cards contam demandas CRIADAS nas últimas 24h
 * (created_at >= now-24h). As listas "Em andamento" / "Aguardando aprovação"
 * abaixo mostram TODAS as abertas agora (sem janela 24h) — subtítulo da UI
 * deixa a diferença explícita pra não parecer bug card×lista.
 */
function derive24hFallback(demands: Demand[], now = Date.now()): Demands24h {
  const since = now - 24 * 3600_000;
  const recent = demands.filter((d) => {
    const t = new Date(d.created_at).getTime();
    return Number.isFinite(t) && t >= since;
  });
  const count = (statuses: string[]) =>
    recent.filter((d) => statuses.includes(d.status)).length;
  return {
    ready: count(['draft', 'planned', 'ready']),
    // Alinhado a IN_PROGRESS_STATUSES / DEMAND_STATUS_TO_DASHBOARD_KEY.
    running: count([...IN_PROGRESS_STATUSES]),
    pending_approval: count([...WAITING_APPROVAL_STATUSES]),
    approved: count(['approved']),
    done: count(['done']),
    rejected: count(['rejected', 'cancelled']),
    blocked: count(['blocked', 'failed']),
  };
}

/**
 * Empty vs erro (DEM-2592): falha de listDemands/overview NÃO pode renderizar
 * o empty "Nenhuma demanda…" — isso mente (zero) quando a API quebrou.
 * Mostra mensagem + botão retry.
 */
function BlockError({
  message,
  onRetry,
  testId,
}: {
  message: string;
  onRetry: () => void;
  testId?: string;
}) {
  return (
    <div
      className="wf-empty flex flex-col items-center gap-2 py-4"
      data-testid={testId}
      role="alert"
    >
      <AlertCircle className="h-4 w-4 text-raios-danger-text" aria-hidden="true" />
      <div className="text-center text-sm text-raios-muted">{message}</div>
      <Button
        variant="ghost"
        size="sm"
        onClick={onRetry}
        className="gap-1.5"
        aria-label="Tentar novamente"
      >
        <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
        Tentar novamente
      </Button>
    </div>
  );
}

// DEM-2048: a derivação de "Etapa X/Y" foi UNIFICADA — a Dashboard consome a
// função canônica `stepInfoFromRun` de mc/demand-step-info.ts (single source of
// truth, com o fix do denominador estável da DEM-2036), em vez da cópia local
// `deriveStepInfo` que divergiu. O card de demanda "em andamento" passa a ser o
// MESMO componente da tela Demandas (DemandRichCard).

// useCoalesced extraído pra @/hooks/useCoalesced (PERF-01) — compartilhado
// com MCDemandsPage.

/* ═══ Blocos compartilhados (anatomia literal do wireframe) ═════════════════ */

/** Header de bloco (wireframe .dblock-h: ícone + título + pill live). */
function BlockHeader({ icon: Icon, title }: { icon: LucideIcon; title: string }) {
  return (
    <div className="wf-dblock-h">
      <Icon className="ic" aria-hidden="true" />
      {title}
      <span className="wf-live">live</span>
    </div>
  );
}

/* ═══ Linha 1 — Card de contagem (wireframe .mc) ════════════════════════════ */

type CountSplitTone = 'draft' | 'active' | 'tenant' | 'global';

function CountCard({
  icon: Icon,
  label,
  testId,
  total,
  splits,
}: {
  icon: LucideIcon;
  label: string;
  testId: string;
  total: number | null;
  splits: Array<{ value: number | null; label: string; tone: CountSplitTone }>;
}) {
  return (
    <div className="wf-mc" data-testid={testId}>
      <div className="wf-mh">
        <Icon className="ic" aria-hidden="true" />
        {label}
      </div>
      <div className="wf-mtot">
        <small>total</small>
        {fmtNumber(total)}
      </div>
      <div className="wf-msplit">
        {splits.map((s) => (
          <div key={s.label} className="wf-ms">
            <div className={`wf-v wf-v--${s.tone}`}>{fmtNumber(s.value)}</div>
            <div className="wf-k">{s.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ═══ Linha 3 — chips 24h (wireframe .statgrid/.stat) ═══════════════════════ */

const STAT_CHIPS: Array<{ key: keyof Demands24h; label: string; tone: WfKpiTone }> = [
  { key: 'ready', label: 'Prontas', tone: 'ready' },
  { key: 'running', label: 'Em execução', tone: 'run' },
  { key: 'pending_approval', label: 'Aguard. aprov.', tone: 'wait' },
  { key: 'approved', label: 'Aprovadas', tone: 'appr' },
  { key: 'done', label: 'Finalizadas', tone: 'done' },
  { key: 'rejected', label: 'Reprovadas', tone: 'rej' },
  { key: 'blocked', label: 'Bloqueadas', tone: 'block' },
];

// DEM-2048: o card "em andamento" (antes InProgressRow inline + deriveStepInfo
// copiado) foi UNIFICADO com a tela Demandas — a renderização agora usa
// <DemandRichCard demand step businessName /> (mc/DemandRichCard.tsx), o MESMO
// componente do board de Demandas, eliminando a divergência. O card permanece
// PRESENTATIONAL PURO: a página resolve demand/step/businessName e os passa por
// props (nenhum fetch dentro do card — anti-N+1).

/* ═══ Atividade recente (wireframe .afeed/.arow — feed mc-store, máx 10) ════ */

// Tom do dot por status (wireframe: default=accent, .ok, .warn, .bad).
const ACTIVITY_DOT_TONE: Record<string, 'ok' | 'warn' | 'bad'> = {
  completed: 'ok',
  failed: 'bad',
  timeout: 'warn',
  partial: 'warn',
};

function ActivityFeed({ items }: { items: ActivityItem[] }) {
  if (items.length === 0) {
    return <WfEmpty>Nenhuma atividade recente.</WfEmpty>;
  }
  return (
    <div className="wf-feed" data-testid="activity-feed">
      {items.map((a) => {
        const tone = ACTIVITY_DOT_TONE[a.status];
        return (
          <div key={`${a.kind}-${a.id}`} className="wf-feed-row">
            <span className={cn('wf-feed-dot', tone && `wf-feed-dot--${tone}`)} aria-hidden="true" />
            {/* F3 2026-07-19: formatador único — "Nome executou como agente". */}
            <span className="wf-feed-text">
              <b>{formatActorLead(a).display}</b> {formatActorLead(a).verb}
              {formatActorLead(a).target && <> <code>{formatActorLead(a).target}</code></>}
            </span>
            <ElapsedClock since={a.started_at} className="wf-feed-time" icon={false} />
          </div>
        );
      })}
    </div>
  );
}

/* ═══ Modais aprovar / reprovar ═════════════════════════════════════════════ */

function ApproveDialog({
  demand,
  onClose,
  onConfirm,
  busy,
}: {
  demand: Demand | null;
  onClose: () => void;
  // DEM-2038: contexto opcional do usuário propagado como `notes` na transição.
  onConfirm: (info: string) => void;
  busy: boolean;
}) {
  const [info, setInfo] = useState('');

  // Reseta o campo a cada abertura (estado fresco — espelha RejectDialog).
  useEffect(() => {
    if (demand) setInfo('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demand?.id]);

  return (
    <Dialog open={demand !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Check className="h-4 w-4 text-raios-success-text" aria-hidden="true" />
            Aprovar demanda
          </DialogTitle>
          <DialogDescription>
            Confirmar aprovação da demanda{' '}
            <span className="font-mono text-raios-accent-text">{demand?.code ?? demand?.id.slice(0, 8)}</span>?
            Ela será concluída e finalizada.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1">
          <span className="text-[10px] uppercase font-semibold tracking-wider text-raios-muted">
            Contexto adicional (opcional)
          </span>
          <Textarea
            placeholder="Observações da aprovação…"
            value={info}
            onChange={(e) => setInfo(e.target.value)}
            rows={3}
            aria-label="Contexto adicional da aprovação"
          />
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button variant="brand" onClick={() => onConfirm(info)} loading={busy}>Aprovar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RejectDialog({
  demand,
  stepOptions,
  onClose,
  onConfirm,
  busy,
}: {
  demand: Demand | null;
  stepOptions: Array<{ value: string; label: string }>;
  onClose: () => void;
  onConfirm: (resend: boolean, info: string, returnStep: string) => void;
  busy: boolean;
}) {
  const [resend, setResend] = useState(false);
  const [info, setInfo] = useState('');
  const [returnStep, setReturnStep] = useState('');

  // Reseta o estado a cada abertura (checkbox OFF por padrão — DASH-07).
  useEffect(() => {
    if (demand) {
      setResend(false);
      setInfo('');
      setReturnStep(stepOptions[0]?.value ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demand?.id]);

  return (
    <Dialog open={demand !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <X className="h-4 w-4 text-raios-danger-text" aria-hidden="true" />
            Reprovar demanda
          </DialogTitle>
          <DialogDescription>
            Confirmar reprovação da demanda{' '}
            <span className="font-mono text-raios-accent-text">{demand?.code ?? demand?.id.slice(0, 8)}</span>?
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <label className="flex items-start gap-2.5 cursor-pointer">
            <Checkbox
              checked={resend}
              onCheckedChange={(c) => setResend(c === true)}
              aria-label="Reenviar ao pipeline"
            />
            <span className="text-sm text-raios-text -mt-0.5">
              Reenviar ao pipeline
              <span className="block text-xs text-raios-muted">
                Marque pra devolver o trabalho a um worker, com instruções e a etapa de retorno.
              </span>
            </span>
          </label>

          {resend && (
            <div className="space-y-3 rounded-lg border border-raios-border bg-raios-elevated/30 p-3">
              <div className="space-y-1">
                <span className="text-[10px] uppercase font-semibold tracking-wider text-raios-muted">
                  Informações adicionais
                </span>
                <Textarea
                  placeholder="O que precisa ser refeito / corrigido…"
                  value={info}
                  onChange={(e) => setInfo(e.target.value)}
                  rows={3}
                />
              </div>
              <div className="space-y-1">
                <span className="text-[10px] uppercase font-semibold tracking-wider text-raios-muted">
                  Etapa de retorno
                </span>
                <Select
                  options={stepOptions.length > 0 ? stepOptions : [{ value: '', label: 'Início do pipeline' }]}
                  value={returnStep}
                  onChange={setReturnStep}
                  placeholder="Etapa"
                />
              </div>
            </div>
          )}

          <p className="text-[11px] text-raios-muted">
            Toda demanda em aprovação/review precisa de uma decisão sua — inclusive
            unsupervised que escalou por não fechar sozinha.
          </p>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button variant="destructive" loading={busy} onClick={() => onConfirm(resend, info, returnStep)}>
            Reprovar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ═══ Page ══════════════════════════════════════════════════════════════════ */

// W1.2: listas enxutas — só o que o dashboard mostra (resto rola na tela Demandas).
const LIST_PER_PAGE = 20;

export function DashboardPage() {
  const { tenant } = useAuth();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState<CountsBlock | null>(null);
  const [topics, setTopics] = useState<TodayTopics | null>(null);
  const [stats24h, setStats24h] = useState<Demands24h | null>(null);
  // DEM-2592: erro honesto — falha de listDemands/overview NÃO vira empty "Nenhuma…".
  const [demandsListError, setDemandsListError] = useState<string | null>(null);
  const [stats24hError, setStats24hError] = useState<string | null>(null);
  const [demands, setDemands] = useState<Demand[]>([]);
  const [runs, setRuns] = useState<PipelineRunSummary[]>([]);
  // DEM-2048: negócios co-carregados COM as demandas (no mesmo Promise.allSettled
  // do loadDemandsBlock) pra montar businessNameById e exibir a badge de business
  // no card SEM pop-in. Antes só eram listados no fallback de contagem
  // (deriveCountsFallback), que descartava os nomes.
  const [businesses, setBusinesses] = useState<Business[]>([]);

  const [detailDemand, setDetailDemand] = useState<Demand | null>(null);
  const [approveDemand, setApproveDemand] = useState<Demand | null>(null);
  const [rejectDemand, setRejectDemand] = useState<Demand | null>(null);
  const [transitionBusy, setTransitionBusy] = useState(false);

  /* ── Data load (W1.2 — 2 blocos independentes, requests em paralelo) ────── */

  // Espelho do estado pro fallback de counts não re-derivar (4 listagens) a
  // cada refetch realtime do overview em backend pré-restart.
  const countsRef = useRef<CountsBlock | null>(null);
  useEffect(() => {
    countsRef.current = counts;
  }, [counts]);
  // DEM-2592: last-known-good da métrica 24h — se overview+fallback falham
  // mas já temos valor, mantém chips; só marca erro se nunca carregou.
  const stats24hRef = useRef<Demands24h | null>(null);
  useEffect(() => {
    stats24hRef.current = stats24h;
  }, [stats24h]);

  /** Bloco overview: counts + assuntos de hoje + métrica 24h (1 request). */
  const loadOverviewBlock = useCallback(async () => {
    let overview: DashboardOverview | null = null;
    try {
      overview = await api.getDashboardOverview();
    } catch (err) {
      console.error('[Dashboard] overview falhou:', err);
    }

    // Assuntos sintetizados pelo backend (cache 15min); null/[] → empty state.
    setTopics(overview?.today_topics ?? null);

    if (overview?.counts) {
      setCounts(overview.counts);
    } else if (countsRef.current === null) {
      // Fallback OBRIGATÓRIO §6.1 (backend pré-restart sem counts): deriva das
      // 4 listagens — só enquanto não temos NENHUM count (é caro).
      setCounts(await deriveCountsFallback());
    }

    if (overview?.demands_24h_by_status) {
      setStats24h(overview.demands_24h_by_status);
      setStats24hError(null);
    } else {
      // Fallback LEVE (W1.2): 1 fetch enxuto só quando o overview falhou ou
      // não envia a métrica (pré-restart) — substitui o antigo per_page:200.
      try {
        const resp = await api.listDemands({ per_page: 100 });
        setStats24h(derive24hFallback(resp.items ?? resp.demands ?? []));
        setStats24hError(null);
      } catch (err) {
        console.error('[Dashboard] fallback métrica 24h falhou:', err);
        // DEM-2592: se nunca carregou a métrica, não mostre chips como zero —
        // UI exibe erro/retry. Se já há last-known-good, mantém.
        if (stats24hRef.current === null) {
          setStats24hError('Não foi possível carregar a métrica 24h.');
        }
      }
    }
  }, []);

  /** Bloco demandas: listas alinhadas ao card KPI (DEM-2592) + runs + negócios,
   *  TODOS no mesmo Promise.allSettled (co-carregamento atômico — DEM-2048).
   *  Status em execução = IN_PROGRESS_STATUSES (running+finishing);
   *  Aguardando = WAITING_APPROVAL_STATUSES (pending_approval+review).
   *  Assim o card só renderiza quando step (runs) E businessName (businesses)
   *  já estão disponíveis, eliminando o pop-in do contador/badge. */
  const loadDemandsBlock = useCallback(async () => {
    // DEM-2592: 1 chamada por status canônico das listas (BE aceita status eq
    // único). finishing entra na lista Em andamento — parity com o card KPI.
    // DEM-2592 fix tsc: tipar cada settled separadamente. Misturar listDemands +
    // listPipelineRuns + listBusinesses no mesmo allSettled vira união e o TS
    // perde .items/.demands (auto-review FAIL).
    const demandListPromises = [
      ...IN_PROGRESS_STATUSES.map((status) =>
        api.listDemands({ status, per_page: LIST_PER_PAGE }),
      ),
      ...WAITING_APPROVAL_STATUSES.map((status) =>
        api.listDemands({ status, per_page: LIST_PER_PAGE }),
      ),
    ];
    const [listResults, runsRes, businessesRes] = await Promise.all([
      Promise.allSettled(demandListPromises),
      Promise.allSettled([listPipelineRuns({ light: true })]).then((r) => r[0]),
      Promise.allSettled([api.listBusinesses()]).then((r) => r[0]),
    ]);

    const items: Demand[] = [];
    let listFailures = 0;
    for (const r of listResults) {
      if (r.status === 'fulfilled') {
        const body = r.value;
        items.push(...(body.items ?? body.demands ?? []));
      } else {
        listFailures += 1;
        console.error('[Dashboard] listDemands falhou:', r.reason);
      }
    }
    // DEM-2592: se TODAS as listagens de demanda falharam → erro/retry (NUNCA
    // empty "Nenhuma demanda…"). Se pelo menos 1 ok, mostra o que veio e limpa erro.
    if (listFailures === listResults.length) {
      setDemandsListError('Não foi possível carregar as demandas.');
      // Não zera lista conhecida em refetch total-fail — last known good.
      // Só na 1ª carga (sem itens) o empty é substituído pelo BlockError.
    } else {
      setDemandsListError(null);
      // Dedupe por id — defensivo caso o backend ignore o filtro de status.
      setDemands([...new Map(items.map((d) => [d.id, d])).values()]);
    }

    if (runsRes.status === 'fulfilled') {
      setRuns(runsRes.value);
    } else {
      console.error('[Dashboard] listPipelineRuns falhou:', runsRes.reason);
      setRuns([]);
    }

    if (businessesRes.status === 'fulfilled') {
      setBusinesses(businessesRes.value);
    } else {
      // "last known good": NÃO zera o map num refetch que falhou — evita a badge
      // de business sumir do card durante uma rajada do realtime.
      console.error('[Dashboard] listBusinesses falhou:', businessesRes.reason);
    }
  }, []);

  const loadData = useCallback(async () => {
    try {
      // W1.2: blocos independentes em PARALELO (antes: cascata de 4 awaits).
      await Promise.all([loadOverviewBlock(), loadDemandsBlock()]);
    } finally {
      setLoading(false);
    }
  }, [loadOverviewBlock, loadDemandsBlock]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  /* ── Realtime — refetch POR BLOCO, coalescido em 2s (W1.2) ──────────────── */

  // PERF-01: janelas de coalesce calibradas pro realtime VIVO (DSH-01) —
  // tasks/pipeline_runs são firehose na plataforma autônoma. Overview são
  // counters/topics (10s de atraso é imperceptível); demands tem patch local
  // IMEDIATO no evento (abaixo), então o refetch de 5s é só reconciliação.
  const refetchOverview = useCoalesced(loadOverviewBlock, 10_000);
  const refetchDemands = useCoalesced(loadDemandsBlock, 5_000);

  // 5 tabelas core (tasks/businesses/pull_requests/llm_accounts/
  // daily_priorities) alimentam counts/topics/métrica → só o bloco overview.
  useRealtimeDashboard(refetchOverview, tenant?.id);
  // Evento em demands → patch local IMEDIATO no card (status/priority/etc.
  // mudam na hora, sem esperar o coalesce de 2s) + refetch coalescido pra
  // reconciliar membership das listas (running/pending_approval/review).
  const demandsChannelStatus = useSupabaseRealtime(
    { table: 'demands', filter: tenant?.id ? `tenant_id=eq.${tenant.id}` : undefined },
    useCallback(
      (payload?: RealtimeEventPayload) => {
        const row = payload?.new as Partial<Demand> | null | undefined;
        if (payload?.eventType === 'UPDATE' && row?.id) {
          setDemands((prev) => prev.map((d) => (d.id === row.id ? { ...d, ...row } : d)));
        }
        refetchDemands();
      },
      [refetchDemands],
    ),
  );

  // DSH-01 resiliência: se o canal de demands não está conectado (socket caiu,
  // auth do realtime indisponível), o bloco de demandas NÃO pode congelar —
  // polling de 30s segura a atualização até o canal voltar.
  useEffect(() => {
    if (demandsChannelStatus === 'connected') return;
    const id = window.setInterval(() => void loadDemandsBlock(), 30_000);
    return () => window.clearInterval(id);
  }, [demandsChannelStatus, loadDemandsBlock]);
  // companion_sessions (na publication do Realtime) → assuntos de hoje.
  useSupabaseRealtime(
    { table: 'companion_sessions', filter: tenant?.id ? `tenant_id=eq.${tenant.id}` : undefined },
    refetchOverview,
  );
  // pipeline_runs (na publication desde 2026-06-11) → badge "Etapa X/Y" e chip
  // de pipeline atualizam quando o run avança/regride de step.
  useSupabaseRealtime(
    { table: 'pipeline_runs', filter: tenant?.id ? `tenant_id=eq.${tenant.id}` : undefined },
    refetchDemands,
  );

  /* ── Atividade recente — feed realtime do mc-store (independente) ───────── */

  const activity = useMCStore((s) => s.activity);
  const fetchActivity = useMCStore((s) => s.fetchActivity);
  const subscribeActivityRealtime = useMCStore((s) => s.subscribeActivityRealtime);
  const unsubscribeActivityRealtime = useMCStore((s) => s.unsubscribeActivityRealtime);

  useEffect(() => {
    void fetchActivity(true);
    if (tenant?.id) void subscribeActivityRealtime(tenant.id);
    return () => unsubscribeActivityRealtime();
  }, [tenant?.id, fetchActivity, subscribeActivityRealtime, unsubscribeActivityRealtime]);

  const recentActivity = useMemo(
    () =>
      [...activity]
        .sort((a, b) => new Date(b.started_at).getTime() - new Date(a.started_at).getTime())
        .slice(0, 10),
    [activity],
  );

  /* ── Listas derivadas ───────────────────────────────────────────────────── */

  const runByDemand = useMemo(() => {
    const map = new Map<string, PipelineRunSummary>();
    for (const run of runs) {
      const prev = map.get(run.demand_id);
      if (!prev || new Date(run.started_at).getTime() > new Date(prev.started_at).getTime()) {
        map.set(run.demand_id, run);
      }
    }
    return map;
  }, [runs]);

  /* ── Nome do negócio por demanda — badge linha 3 do card (DEM-2036/2048) ──
     A busca SOBE pra página (uma vez, no loadDemandsBlock) e DESCE ao card via
     este map (anti-N+1). Mesmo padrão de MCDemandsPage/DemandMobileColumn:
     index id→name dos businesses, resolvido por demand.business_id. */
  const businessNameById = useMemo(() => {
    const byId = new Map(businesses.map((b) => [b.id, b.name]));
    const map = new Map<string, string | null>();
    for (const d of demands) {
      map.set(d.id, d.business_id ? (byId.get(d.business_id) ?? null) : null);
    }
    return map;
  }, [businesses, demands]);

  // DEM-2592: FONTE ÚNICA (demandIsInProgress) — running + finishing, alinhado
  // ao card KPI (DEMAND_STATUS_TO_DASHBOARD_KEY) e ao kanban DEMAND_COLUMNS.
  // Antes só `running` → finishing sumia da lista enquanto o card ainda contava.
  const inProgress = useMemo(
    () =>
      demands
        .filter((d) => demandIsInProgress(d))
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()),
    [demands],
  );

  // FONTE ÚNICA (demandAwaitsApproval): pending_approval/review aparecem pra aprovar/
  // reprovar, INDEPENDENTE de supervision_mode — uma unsupervised que escalou pra
  // aprovação também precisa do card+botões (senão fica zumbi, esperando sem ação possível).
  const waitingApproval = useMemo(
    () =>
      demands
        .filter((d) => demandAwaitsApproval(d))
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()),
    [demands],
  );

  const rejectStepOptions = useMemo(() => {
    if (!rejectDemand) return [];
    const run = runByDemand.get(rejectDemand.id);
    const steps = (run?.config?.['steps'] as Array<{ id?: string; name?: string }> | undefined) ?? [];
    return steps.map((s, i) => ({
      value: s.id ?? String(i + 1),
      label: `${i + 1} · ${s.name ?? s.id ?? `Etapa ${i + 1}`}`,
    }));
  }, [rejectDemand, runByDemand]);

  /* ── Transições (PATCH /v1/demands/:id/transition) ──────────────────────── */

  const handleApproveConfirm = useCallback(
    async (info: string) => {
      if (!approveDemand) return;
      setTransitionBusy(true);
      try {
        // DEM-2038: contexto opcional vai como feedback { notes } na transição.
        // Backend ANTIGO (pré-restart) usa Zod estrito e rejeita o campo extra
        // com 4xx → degrada pra transição simples (mesmo padrão da reprovação).
        const trimmed = info.trim();
        if (trimmed) {
          try {
            await api.transitionDemand(approveDemand.id, 'approved', { notes: trimmed });
          } catch (err) {
            if (err instanceof ApiError && err.status >= 400 && err.status < 500) {
              await api.transitionDemand(approveDemand.id, 'approved');
              toast({
                type: 'warning',
                message:
                  'Demanda aprovada, mas o contexto não foi registrado (backend ainda não aceita feedback na transição).',
              });
            } else {
              throw err;
            }
          }
        } else {
          await api.transitionDemand(approveDemand.id, 'approved');
        }
        setApproveDemand(null);
        void loadData();
      } catch (err) {
        console.error('[Dashboard] aprovar falhou:', err);
      } finally {
        setTransitionBusy(false);
      }
    },
    [approveDemand, loadData, toast],
  );

  const handleRejectConfirm = useCallback(
    async (resend: boolean, info: string, returnStep: string) => {
      if (!rejectDemand) return;
      setTransitionBusy(true);
      try {
        // Contrato novo: PATCH /v1/demands/:id/transition aceita
        // { status, feedback: { notes, return_step, resubmit } } e persiste em
        // metadata.rejection_feedback. Backend ANTIGO usa Zod estrito e
        // rejeita o campo extra com 4xx → defensivo: re-tenta sem feedback e
        // avisa que o reenvio não foi registrado (pré-restart).
        if (resend) {
          const feedback = {
            ...(info.trim() ? { notes: info } : {}),
            ...(returnStep ? { return_step: returnStep } : {}),
            resubmit: true,
          };
          try {
            await api.transitionDemand(rejectDemand.id, 'rejected', feedback);
          } catch (err) {
            if (err instanceof ApiError && err.status >= 400 && err.status < 500) {
              await api.transitionDemand(rejectDemand.id, 'rejected');
              toast({
                type: 'warning',
                message:
                  'Demanda reprovada, mas o reenvio ao pipeline não foi registrado (backend ainda não aceita feedback na transição).',
              });
            } else {
              throw err;
            }
          }
        } else {
          await api.transitionDemand(rejectDemand.id, 'rejected');
        }
        setRejectDemand(null);
        void loadData();
      } catch (err) {
        console.error('[Dashboard] reprovar falhou:', err);
      } finally {
        setTransitionBusy(false);
      }
    },
    [rejectDemand, loadData, toast],
  );

  /* ── Skeleton ───────────────────────────────────────────────────────────── */

  if (loading) {
    return (
      <div className="space-y-6" aria-busy="true" data-testid="dashboard-skeleton">
        <div className="flex justify-between items-end">
          <div className="space-y-2">
            <Skeleton className="h-8 w-48 rounded-lg" />
            <Skeleton className="h-4 w-64 rounded-md" />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-32 rounded-lg" />
          ))}
        </div>
        <Skeleton className="h-36 rounded-lg" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          <Skeleton className="h-[420px] rounded-lg lg:col-span-2" />
          <Skeleton className="h-[420px] rounded-lg" />
        </div>
      </div>
    );
  }

  /* ── Content — réplica literal do wireframe "02 Dashboard" (.dash) ──────── */

  return (
    <div className="space-y-4">
      <PageHeader
        title="Dashboard"
        description="Visão geral da sua conta"
        actions={
          <div className="flex items-center gap-3">
            {tenant?.is_admin && <Badge variant="warning">Admin</Badge>}
            <span className="label-tech text-raios-text-disabled">
              {fmtDayTime(new Date())}
            </span>
          </div>
        }
      />

      <div className="wf-dash">
        {/* ═══ LINHA 1: 4 CARDS (wireframe .dash-cards) ═══ */}
        <section aria-label="Contagens da conta" className="wf-dash-cards">
          <CountCard
            icon={Briefcase}
            label="Negócios"
            testId="count-businesses"
            total={counts?.businesses.total ?? null}
            splits={[
              { value: counts?.businesses.draft ?? null, label: 'draft', tone: 'draft' },
              { value: counts?.businesses.active ?? null, label: 'ativos', tone: 'active' },
            ]}
          />
          <CountCard
            icon={Bot}
            label="Agentes"
            testId="count-agents"
            total={counts?.agents.total ?? null}
            splits={[
              { value: counts?.agents.tenant ?? null, label: 'tenant', tone: 'tenant' },
              { value: counts?.agents.global ?? null, label: 'globais', tone: 'global' },
            ]}
          />
          <CountCard
            icon={Sparkles}
            label="Skills"
            testId="count-skills"
            total={counts?.skills.total ?? null}
            splits={[
              { value: counts?.skills.tenant ?? null, label: 'tenant', tone: 'tenant' },
              { value: counts?.skills.global ?? null, label: 'globais', tone: 'global' },
            ]}
          />
          <CountCard
            icon={Workflow}
            label="Pipelines"
            testId="count-pipelines"
            total={counts?.pipelines.total ?? null}
            splits={[
              { value: counts?.pipelines.tenant ?? null, label: 'tenant', tone: 'tenant' },
              { value: counts?.pipelines.global ?? null, label: 'globais', tone: 'global' },
            ]}
          />
        </section>

        {/* ═══ LINHA 2: SÓ ASSUNTOS DE HOJE (wireframe .dash-mid) ═══ */}
        <div className="wf-dash-mid">
          <section aria-label="Assuntos principais de hoje" className="wf-dblock">
            <BlockHeader icon={MessagesSquare} title="Assuntos principais de hoje" />
            {topics && topics.length > 0 ? (
              <div className="wf-topics">
                {topics.slice(0, 3).map((t, i) => (
                  <div key={t.topic} className="wf-topic">
                    <div className="wf-rank">#{i + 1}</div>
                    <div className="wf-tt">{t.topic}</div>
                    <div className="wf-tm">
                      {fmtNumber(t.sessions_count)} {t.sessions_count === 1 ? 'sessão' : 'sessões'} hoje ·{' '}
                      {fmtNumber(t.messages_count)} {t.messages_count === 1 ? 'mensagem' : 'mensagens'}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              // Backend sintetiza os assuntos (cache 15min); cache vazio → empty state.
              <WfEmpty>Sem assuntos extraídos ainda.</WfEmpty>
            )}
          </section>
        </div>

        {/* ═══ LINHA 3: DEMANDAS (24h + 2 listas) + ATIVIDADE (wireframe .dbottom).
             C1: grid stretch (SEM items-start) + listas com altura fixa
             (.wf-dscroll) → o bloco Demandas não encolhe e a coluna Atividade
             acompanha a altura. ═══ */}
        <div className="wf-dbottom" data-testid="dash-bottom">
          <section aria-label="Demandas" className="wf-dblock">
            <BlockHeader icon={Package} title="Demandas" />
            {/* DEM-2592: semântica explícita — cards = criadas nas últimas 24h;
                listas = abertas agora (sem janela). Evita contradição visual
                card×lista quando uma demanda running tem created_at > 24h. */}
            <p className="wf-dblock-sub" data-testid="demands-24h-subtitle">
              Cards: criadas nas últimas 24h · Listas: abertas agora
            </p>
            {stats24hError && stats24h === null ? (
              <BlockError
                testId="stats24h-error"
                message={stats24hError}
                onRetry={() => void loadOverviewBlock()}
              />
            ) : (
              <div className="wf-statgrid">
                {STAT_CHIPS.map((c) => (
                  <WfKpi
                    key={c.key}
                    data-testid={`stat-${c.key}`}
                    tone={c.tone}
                    value={fmtNumber(stats24h?.[c.key] ?? null)}
                    label={c.label}
                  />
                ))}
              </div>
            )}

            <div className="wf-twolist">
              {/* LISTA ESQUERDA: EM ANDAMENTO (running + finishing) */}
              <div className="wf-dlist">
                <div className="wf-lh">Em andamento</div>
                <div className="wf-dscroll" data-testid="inprogress-scroll">
                  {demandsListError && inProgress.length === 0 ? (
                    <BlockError
                      testId="inprogress-error"
                      message={demandsListError}
                      onRetry={() => void loadDemandsBlock()}
                    />
                  ) : inProgress.length === 0 ? (
                    <WfEmpty>Nenhuma demanda em execução.</WfEmpty>
                  ) : (
                    inProgress.map((d) => (
                      // DEM-2048: MESMO card da tela Demandas (single source of
                      // truth). step e businessName descem por props já
                      // resolvidos pela página (sem fetch interno no card).
                      // `projects` é OPCIONAL e fora de escopo aqui (fallback do
                      // chip quando a demanda não tem pipeline) — omitir não
                      // quebra; o chip cai pro "—" do DemandRichCard.
                      <DemandRichCard
                        key={d.id}
                        demand={d}
                        onClick={setDetailDemand}
                        step={stepInfoFromRun(runByDemand.get(d.id))}
                        businessName={businessNameById.get(d.id) ?? null}
                      />
                    ))
                  )}
                </div>
              </div>
              {/* LISTA DIREITA: AGUARDANDO APROVAÇÃO (pending_approval + review) */}
              <div className="wf-dlist">
                <div className="wf-lh">Aguardando aprovação</div>
                <div className="wf-dscroll" data-testid="approval-scroll">
                  {demandsListError && waitingApproval.length === 0 ? (
                    <BlockError
                      testId="approval-error"
                      message={demandsListError}
                      onRetry={() => void loadDemandsBlock()}
                    />
                  ) : waitingApproval.length === 0 ? (
                    <WfEmpty>Nenhuma demanda aguardando aprovação.</WfEmpty>
                  ) : (
                    waitingApproval.map((d) => (
                      <WaitingApprovalCard
                        key={d.id}
                        demand={d}
                        meta={
                          <div className="wf-dmeta">
                            <span>
                              <Clock className="ic" aria-hidden="true" />
                              Criada {fmtDayTime(d.created_at)}
                            </span>
                            {d.updated_at && <span>Em aprov. {fmtTimeOnly(d.updated_at)}</span>}
                            <ElapsedClock since={d.updated_at ?? d.created_at} />
                          </div>
                        }
                        onOpen={setDetailDemand}
                        onApprove={setApproveDemand}
                        onReject={setRejectDemand}
                      />
                    ))
                  )}
                </div>
              </div>
            </div>
          </section>

          {/* ATIVIDADE RECENTE (wireframe .dblock.fill) */}
          <section aria-label="Atividade recente" className="wf-dblock wf-dblock--fill">
            <BlockHeader icon={ActivityIcon} title="Atividade recente" />
            <ActivityFeed items={recentActivity} />
          </section>
        </div>
      </div>

      {/* ═══ Modais ═══ */}
      <DemandDetailPanel
        // Demand VIVA: lookup na lista realtime (status muda com o run); o
        // snapshot do clique é só fallback (ex: saiu das listas da dashboard).
        demand={detailDemand ? (demands.find((d) => d.id === detailDemand.id) ?? detailDemand) : null}
        onClose={() => setDetailDemand(null)}
      />
      <ApproveDialog
        demand={approveDemand}
        onClose={() => setApproveDemand(null)}
        onConfirm={(info) => void handleApproveConfirm(info)}
        busy={transitionBusy}
      />
      <RejectDialog
        demand={rejectDemand}
        stepOptions={rejectStepOptions}
        onClose={() => setRejectDemand(null)}
        onConfirm={(resend, info, returnStep) => void handleRejectConfirm(resend, info, returnStep)}
        busy={transitionBusy}
      />
    </div>
  );
}
